import 'inter-ui'
import React, { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { HashRouter } from 'react-router-dom'

import App from './pages/App'
import store from './state'
import * as serviceWorkerRegistration from './serviceWorkerRegistration'
import ApplicationUpdater from './state/application/updater'
import ListsUpdater from './state/lists/updater'
import MulticallUpdater from './state/multicall/updater'
import TransactionUpdater from './state/transactions/updater'
import UserUpdater from './state/user/updater'
import ThemeProvider, { FixedGlobalStyle, ThemedGlobalStyle } from './theme'
import * as Sentry from '@sentry/react'
import { PrivyProvider } from '@privy-io/react-auth'
import { defineChain } from 'viem'

Sentry.init({
  dsn: 'https://3e37689f39b06d622bcb03fb6bc91d21@o4508704864403456.ingest.us.sentry.io/4508704866631680',
  release: `v${process.env.REACT_APP_VERSION} HyperSwap Interface`,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.captureConsoleIntegration(),
    Sentry.extraErrorDataIntegration(),
    Sentry.functionToStringIntegration(),
    Sentry.globalHandlersIntegration({ onerror: true, onunhandledrejection: true }),
    Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    /^https:\/\/testnet.hyperswap.exchange/,
    /^https:\/\/hyperswap.exchange/,
    /^https:\/\/mellifluous-otter-7b7bfc.netlify.app/,
  ],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.,
  beforeSend(event: any) {
    const ignoredPatterns = [
      /UNSAFE_componentWillMount/,
      /is not a prop/,
      /Fetching chunk/,
      /fetch multicall chunk/,
      /Calls errored in fetch/,
      /Backpack/,
      /network changed/,
      /Failed to approve token/,
      /Rage click/,
      /window.web3/,
      /Unsupported chain Id/,
      /exceeded the quota/,
      /QuotaExceededError/,
      /Failed to execute/,
    ]

    if (event.message && ignoredPatterns.some((pattern) => pattern.test(event.message))) {
      return null
    }

    return event
  },
})

function Updaters() {
  return (
    <>
      <ListsUpdater />
      <UserUpdater />
      <ApplicationUpdater />
      <TransactionUpdater />
      <MulticallUpdater />
    </>
  )
}

const container = document.getElementById('root')
if (!container) throw new Error('Failed to find the root element')
const root = createRoot(container)

// @ts-ignore
const hyperevm = defineChain({
  id: 999,
  caipNetworkId: 'eip155:999',
  chainNamespace: 'eip155',
  name: 'HyperEVM',
  nativeCurrency: {
    decimals: 18,
    name: 'HYPE',
    symbol: 'HYPE',
  },
  rpcUrls: {
    default: {
      http: ['https://rpc.hyperliquid.xyz/evm'],
    },
  },
  blockExplorers: {
    default: { name: 'Purrsec', url: 'https://purrsec.com' },
  },
})

const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
const privyAppId = isLocalhost ? 'cm9nd7rbe00f4kz0o0wf7s763' : 'cm9ltx0pd01f9l20mybrxvs9b'

const clientId = isLocalhost
  ? 'client-WY5ivZuogR4pjDqAYabmTpAcWnt4NedPhyq2YAFroyGhB'
  : 'client-WY5ivBQVPUKjAeZZYm8YeUAbAa5mHyWuTUEQ8gGTJeJCx'

root.render(
  <StrictMode>
    <FixedGlobalStyle />
    <PrivyProvider
      appId={privyAppId}
      clientId={clientId}
      config={{
        defaultChain: hyperevm,
        supportedChains: [hyperevm],

        appearance: {
          theme: 'dark',
          logo: 'https://raw.githubusercontent.com/HyperSwapX/media-kit/refs/heads/main/PNG-SVG/png/Logo%20White%20%2B%20Horizontal.png',
          walletList: [
            'detected_ethereum_wallets',
            'metamask',
            'wallet_connect',
            'rabby_wallet',
            'safe',
            'coinbase_wallet',
          ],
        },
      }}
    >
      <Provider store={store}>
        <HashRouter>
          <Updaters />
          <ThemeProvider>
            <ThemedGlobalStyle />
            <App />
          </ThemeProvider>
        </HashRouter>
      </Provider>
    </PrivyProvider>
  </StrictMode>
)

serviceWorkerRegistration.unregister()
